import { Space } from "@highnote/server/src/core/entities";
import { SpacesProvider, useSpaces } from "App/components/useEntities";
import React, { createContext, useContext, useMemo } from "react";
import { orderBy, where } from "firebase/firestore";
import { useAuth } from "App/components/Auth";
import { useMembershipFlags } from "App/components/MembershipFlagsContext";
import { useMyMemberships } from "App/components/Auth/MyMembershipsContext";
import { SpaceStub, membershipEntityToSpaceStub } from "./spaceStub";

type TopLevelSpacesContextValue = {
  /**
   * The list every surface renders from. Under the new read model each entry
   * is a `SpaceStub` built from a member document, with no role data at all.
   * A real `Space` satisfies `SpaceStub`, so the legacy path serves the same
   * consumers unchanged. A screen that needs more than the stub's seven
   * fields must fetch the space document; see `LazySpaceActions` for the
   * pattern.
   */
  topLevelSpaces: SpaceStub[];
  /**
   * The legacy space documents, for lookups that must not answer with a
   * stub. Empty under the new read model, so `getGlobalSpace`'s fallback
   * finds nothing there, which is the correct answer: a document this client
   * never fetched is not available.
   */
  topLevelSpaceDocs: Space[];
  topLevelSpacesLoading: boolean;
  totalLimitTopLevelSpaces: number;
  loadMoreTopLevelSpaces: () => void;
};

const TopLevelSpacesContext = createContext<TopLevelSpacesContextValue>({
  topLevelSpaces: [],
  topLevelSpaceDocs: [],
  topLevelSpacesLoading: true,
  totalLimitTopLevelSpaces: 0,
  loadMoreTopLevelSpaces: () => undefined,
});

const _TopLevelSpacesContextProvider = ({
  membersV2Enabled,
  children,
}: {
  membersV2Enabled: boolean;
  children: React.ReactNode;
}) => {
  const {
    entities: legacySpaces,
    loading: legacyLoading,
    loadMore: legacyLoadMore,
    totalLimit: legacyTotalLimit,
  } = useSpaces();
  const { roots, rootsLoading, rootsTotalLimit, loadMoreRoots } =
    useMyMemberships();

  const stubRoots = useMemo(
    () => roots.map(membershipEntityToSpaceStub),
    [roots],
  );

  const value = useMemo(() => {
    if (!membersV2Enabled) {
      return {
        topLevelSpaces: legacySpaces,
        topLevelSpaceDocs: legacySpaces,
        topLevelSpacesLoading: legacyLoading,
        loadMoreTopLevelSpaces: legacyLoadMore,
        totalLimitTopLevelSpaces: legacyTotalLimit,
      };
    }
    return {
      topLevelSpaces: stubRoots,
      topLevelSpaceDocs: [],
      topLevelSpacesLoading: rootsLoading,
      loadMoreTopLevelSpaces: loadMoreRoots,
      totalLimitTopLevelSpaces: rootsTotalLimit,
    };
  }, [
    membersV2Enabled,
    legacySpaces,
    legacyLoading,
    legacyLoadMore,
    legacyTotalLimit,
    stubRoots,
    rootsLoading,
    rootsTotalLimit,
    loadMoreRoots,
  ]);

  return (
    <TopLevelSpacesContext.Provider value={value}>
      {children}
    </TopLevelSpacesContext.Provider>
  );
};

export const TopLevelSpacesContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useAuth();
  /*
   * The sidebar read, moved to member documents. The member-document query
   * (`memberId == authId && isTopLevel == true`, `orderBy spaceName asc`) is
   * the exact mirror of the legacy one. `isTopLevel` means "the member can
   * read the space and inherits nothing", which is what `topLevelReadableBy`
   * encoded. `spaceName` is a byte-for-byte copy of `space.name`. Firestore
   * orders both by UTF-8 bytes, so the two orderings are identical, not
   * merely similar.
   */
  const { membersV2Enabled } = useMembershipFlags();

  const constraints = useMemo(() => {
    /*
     * Under the flag the client does not issue the legacy query. If that
     * query stays open, the client continues to pay for 100 full space
     * documents, which is the whole cost this change removes.
     * `EntitiesContextProvider` treats an empty constraint list as
     * "loading: false, entities: []" with no listener, which is exactly what
     * we want.
     */
    if (membersV2Enabled || !user?.id) return [];
    // PUBLIC_ID should not be used here as doing so will fetch all spaces that are public
    return [
      where("topLevelReadableBy", "array-contains-any", [user.id]),
      orderBy("name", "asc"),
    ];
  }, [user?.id, membersV2Enabled]);

  return (
    <SpacesProvider constraints={constraints} limit={100}>
      <_TopLevelSpacesContextProvider membersV2Enabled={membersV2Enabled}>
        {children}
      </_TopLevelSpacesContextProvider>
    </SpacesProvider>
  );
};

export const useTopLevelSpaces = () => useContext(TopLevelSpacesContext);
